import React, { useEffect } from 'react';
import { element } from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import List from '@material-ui/core/List';
import CssBaseline from '@material-ui/core/CssBaseline';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import Badge from '@material-ui/core/Badge';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import SecurityIcon from '@material-ui/icons/Security';
import { MdDashboard, MdChat } from 'react-icons/md';
import { IoIosDocument } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Modal from '../components/Modal';
import ReportsList from '../components/List';
import ModuleSelector from 'components/ModuleSelector';

import { Icon } from 'styles/components';
import Images from 'assets/img';
import AuthActions from 'store/ducks/auth';
import ReportsActions from 'store/ducks/reports';
import ModulesActions from 'store/ducks/modules';
import ProfilesActions from 'store/ducks/profiles';
import { getCurrentModuleFromStorage, MODULES, saveCurrentModuleToStorage } from 'config/modules';
import { usePermissions } from 'hooks/usePermissions';

import {
  useStyles,
  Logo,
  ContainerProfile,
  WrapperProfile,
  ProfileImg,
  PopoverRow,
  ProfileIcon,
  ExitIcon,
  PopoverText,
} from './styles';

export default function DefaultLayout({ children }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [openProfile, setOpenProfile] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [reportsEl, setReportsEl] = React.useState(null);
  const [moduleSelectorEl, setModuleSelectorEl] = React.useState(null);
  const userActive = useSelector((state) => state.auth.userActive);
  const { reports } = useSelector((state) => state.reports);
  const { currentModule } = useSelector((state) => state.modules);
  const { hasModuleAccess, canView, loading: permissionsLoading, permissions } = usePermissions();

  useEffect(() => {
    dispatch(ReportsActions.getReportsRequest());
    
    // Carregar permissões do usuário
    if (userActive && userActive.id) {
      dispatch(ProfilesActions.getMyPermissionsRequest());
    }
    
    // Carregar módulo do localStorage na inicialização
    const storedModule = getCurrentModuleFromStorage();
    if (storedModule) {
      dispatch(ModulesActions.setModuleSuccess(storedModule));
    } else {
      // Se não houver módulo salvo, usar o módulo de Transporte como padrão
      dispatch(ModulesActions.setModuleSuccess(MODULES.TRANSPORTE));
    }
  }, [dispatch, userActive]);

  // Redirecionar automaticamente baseado em permissões de módulo
  useEffect(() => {
    if (permissionsLoading || !userActive) return;

    const currentPath = history.location.pathname;

    const getModuleDashboardPath = (module) => {
      const dashboardMenu = module?.menus?.find((m) => String(m.label || '').toLowerCase() === 'dashboard');
      return dashboardMenu?.path || '/dashboard';
    };

    const getFirstAvailableModule = () => {
      return Object.values(MODULES).find((m) => hasModuleAccess(m.id)) || null;
    };

    // Se não tem acesso ao módulo atual, redirecionar
    if (currentModule) {
      const hasCurrentModuleAccess = hasModuleAccess(currentModule.id);
      
      if (!hasCurrentModuleAccess) {
        const nextModule = getFirstAvailableModule();
        if (nextModule) {
          dispatch(ModulesActions.setModuleSuccess(nextModule));
          saveCurrentModuleToStorage(nextModule.id);
          history.push(getModuleDashboardPath(nextModule));
        }
      }
    } else {
      // Se não tem módulo selecionado, escolher baseado em permissões
      const nextModule = getFirstAvailableModule();
      if (nextModule) {
        dispatch(ModulesActions.setModuleSuccess(nextModule));
        saveCurrentModuleToStorage(nextModule.id);
        if (currentPath === '/' || currentPath === '/signin') {
          history.push(getModuleDashboardPath(nextModule));
        }
      }
    }
  }, [permissionsLoading, hasModuleAccess, currentModule, userActive, dispatch, history]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  function openProfileMenu() {
    handlePopoverClose();
    setOpenProfile(true);
  }

  const handleReports = (event) => {
    setReportsEl(event.currentTarget);
  };

  const handleCloseReports = () => {
    dispatch(ReportsActions.setReportOldAllRequest());
    setReportsEl(null);
  };

  function handleSetReportOld(reportId) {
    dispatch(ReportsActions.setReportOldRequest(reportId));
    setReportsEl(null);
  }

  const handleModuleSelectorOpen = (event) => {
    setModuleSelectorEl(event.currentTarget);
  };

  const handleModuleSelectorClose = () => {
    setModuleSelectorEl(null);
  };

  const reportsRender = reports.filter(
    (report) => !report.read || report.read === undefined
  );

  // Mapeamento de paths para tipos de cadastro
  const pathToRegistrationType = {
    '/dashboard': null, // Dashboard sempre permitido
    '/trip': 'viagens',
    '/trip/create': 'viagens',
    '/trip/edit': 'viagens',
    '/transport/pedidos-frete': 'pedidos_frete',
    '/embarcadores/dashboard': null, // dashboard do módulo Embarcadores
    '/embarcadores/list': 'embarcadores',
    '/embarcadores/fretes': 'fretes',
    '/embarcadores/fretes/new': 'fretes',
    '/embarcadores/fretes/edit': 'fretes',
    '/embarcadores/fretes/view': 'fretes',
    '/embarcadores/acompanhamento-carga': 'acompanhamento_carga',
    '/trucker': 'motoristas',
    '/providers': 'fornecedores',
    '/insurances': 'seguros',
    '/cargas': 'cargas',
    '/collaborators': 'colaboradores',
    '/profiles': 'perfis',
    '/antecipations': null, // Verificar depois se precisa de permissão específica
    '/financeiro/dashboard': null,
    '/financeiro/contas-pagar': 'contas_pagar',
    '/financeiro/contas-receber': 'contas_receber',
    '/financeiro/clientes': 'clientes',
    '/financeiro/naturezas': 'naturezas',
    '/financeiro/fornecedores': 'fornecedores_financeiro',
    '/financeiro/fluxo-caixa': 'fluxo_caixa',
  };

  // Obter menus do módulo atual ou usar módulo padrão
  const allModuleMenus = currentModule?.menus || MODULES.TRANSPORTE.menus;

  // Filtrar menus baseado em permissões do perfil do usuário
  const currentModuleMenus = React.useMemo(() => {
    // Se ainda está carregando permissões, não mostrar menus (ou mostrar apenas dashboard)
    // Isso evita mostrar menus que o usuário não tem permissão antes das permissões carregarem
    if (permissionsLoading) {
      // Mostrar apenas dashboard enquanto carrega
      const dashboardMenu = allModuleMenus.find(menu => menu.path === '/dashboard' || menu.path.includes('dashboard'));
      return dashboardMenu ? [dashboardMenu] : [];
    }

    // Verificar se o usuário tem acesso ao módulo
    if (currentModule && !hasModuleAccess(currentModule.id)) {
      // Se não tem acesso ao módulo, mostrar apenas Dashboard se existir
      const dashboardMenu = allModuleMenus.find(menu => menu.path === '/dashboard' || menu.path.includes('dashboard'));
      return dashboardMenu ? [dashboardMenu] : [];
    }

    // Filtrar menus baseado em permissões de cadastro do perfil
    const filteredMenus = allModuleMenus.filter((menuItem) => {
      const registrationType = pathToRegistrationType[menuItem.path];
      
      // Se não tem mapeamento (null), sempre permitir (ex: dashboard, antecipations)
      if (registrationType === null) {
        return true;
      }

      // Se não está no mapeamento, verificar se é dashboard ou negar por padrão
      if (registrationType === undefined) {
        // Se contém 'dashboard' no path, sempre permitir
        if (menuItem.path.includes('dashboard')) {
          return true;
        }
        // Para outros paths não mapeados, negar por padrão (segurança)
        console.warn(`⚠️ [DefaultLayout] Path não mapeado: ${menuItem.path}`);
        return false;
      }

      // Verificar se tem permissão de visualização para este tipo de cadastro
      const hasViewPermission = canView(registrationType);
      
      // Debug específico para acompanhamento_carga
      if (registrationType === 'acompanhamento_carga') {
        const registrationFound = permissions?.registrations?.find(r => r.registration_type === registrationType);
        console.log(`🔍 [DefaultLayout] Verificando menu "${menuItem.label}":`, {
          path: menuItem.path,
          registrationType,
          hasViewPermission,
          permissionsLoading,
          allRegistrationTypes: permissions?.registrations?.map(r => r.registration_type) || [],
          registrationFound: registrationFound,
          canViewValue: registrationFound?.can_view,
          currentModuleId: currentModule?.id,
          hasModuleAccess: currentModule ? hasModuleAccess(currentModule.id) : false
        });
      }
      
      // logs de debug removidos (console.log). Mantido apenas warning para casos relevantes.
      if (!hasViewPermission && registrationType) {
        console.warn(`Menu "${menuItem.label}" não será exibido. O perfil não tem permissão de visualização para "${registrationType}". Edite o perfil e salve novamente para adicionar automaticamente.`);
      }
      
      return hasViewPermission;
    });

    // logs de debug removidos

    return filteredMenus;
  }, [allModuleMenus, currentModule, hasModuleAccess, canView, permissionsLoading, permissions]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, {
              [classes.hide]: open,
            })}
          >
            <MenuIcon />
          </IconButton>
          <Logo 
            src={Images.Logo} 
            onClick={handleModuleSelectorOpen}
            style={{ cursor: 'pointer' }}
          />
          <ContainerProfile>
            <Tooltip title="Reportes">
              <IconButton color="inherit" onClick={handleReports}>
                <Badge badgeContent={reportsRender.length} color="secondary" overlap="rectangular">
                  <AnnouncementIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <WrapperProfile onClick={handlePopoverOpen}>
              <ProfileImg
                src={userActive.profileImg || Images.IconProfileImg}
              />
            </WrapperProfile>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <PopoverRow onClick={openProfileMenu}>
                <ProfileIcon />
                <PopoverText>Perfil</PopoverText>
              </PopoverRow>
              <PopoverRow onClick={() => dispatch(AuthActions.signOut())}>
                <ExitIcon />
                <PopoverText>Sair</PopoverText>
              </PopoverRow>
            </Popover>

            <Popover
              anchorEl={reportsEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              onClose={handleCloseReports}
              open={Boolean(reportsEl)}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <ReportsList
                reports={reportsRender}
                onSelect={handleSetReportOld}
              />
            </Popover>
          </ContainerProfile>
        </Toolbar>
      </AppBar>
      <ModuleSelector
        anchorEl={moduleSelectorEl}
        open={Boolean(moduleSelectorEl)}
        onClose={handleModuleSelectorClose}
      />
      {/* <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => history.push('/profile')}>Perfil</MenuItem>
        <MenuItem onClick={() => dispatch(AuthActions.signOut())}>
          Sair
        </MenuItem>
      </Menu> */}
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div className={classes.toolbar}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </div>
        <Divider />
        <List>
          {currentModuleMenus.map((menuItem) => {
            const IconComponent = menuItem.icon;
            
            return (
              <ListItem 
                key={menuItem.path} 
                button 
                component={NavLink} 
                to={menuItem.path}
                activeClassName="Mui-selected"
              >
                <ListItemIcon>
                  {menuItem.icon === 'custom' && menuItem.customIcon ? (
                    // Componente React customizado (ex: Icon do styled-components)
                    menuItem.customIcon()
                  ) : (
                    // Ícone do react-icons ou Material-UI
                    <IconComponent size={20} style={{ fontSize: 20 }} />
                  )}
                </ListItemIcon>
                <ListItemText primary={menuItem.label} />
              </ListItem>
            );
          })}
        </List>
        <Divider />
      </Drawer>
      <Modal
        data={userActive}
        open={openProfile}
        onClose={() => setOpenProfile(false)}
      />
      <main className={classes.content}>
        <div className={classes.toolbar} />
        {children}
      </main>
    </div>
  );
}

DefaultLayout.propTypes = {
  children: element.isRequired,
};
